import { PublicClientApplication, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { LoginResponse } from './useLoginMutation';
import { useAuthToken } from './useAuthToken';
import { logger } from '../utils/logger';

const SocialAuthMutationGQL = gql`
  mutation socialLogin($input: SocialLoginInput!) {
    socialLogin(input: $input) {
      token, uri, sid, verified, email, id
    }
  }
`;

const msalConfig = {
  auth: {
    clientId: '92b224c7-fd7e-42e2-9c68-597d31d82814',
    authority: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    redirectUri: `${window.origin}/login` // Adjust accordingly for your app
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  }
};

// Create an instance of the MSAL application
const msalInstance = new PublicClientApplication(msalConfig);

export default function useMicrosoftAuth(type: 'Login' | 'Registration') {
  const [isInitialized, setInitialized] = useState<boolean>(false);
  const [microsoftAuthLoading, setMicrosoftAuthLoading] = useState(false);
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();
  const [microsoftUser, setMicrosoftUser] = useState<{accessToken: string; email: string; family_name: string, given_name: string}>()

  const { identify, trackEvent, segment } = useAnalytics();

  const [mutation, { error }] = useMutation<{ socialLogin: LoginResponse }>(SocialAuthMutationGQL, {
    onCompleted: ({ socialLogin }) => {
      if (socialLogin.token) {
        removeAuthToken();
        setAuthToken(socialLogin.token, true);
        if (socialLogin.id) {
          segment?.identify(socialLogin.id);
          identify(socialLogin.id, {
            email: socialLogin.email,
          });
        }
        const d = { email: socialLogin.email, loginType: 'Microsoft', userId: socialLogin?.id };
        trackEvent(Events.actions.logIn, d);
        if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {          
          const sharePath = search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`)
          push(sharePath);  
        } else {
          push('/');
        }
      } else if (socialLogin.sid) {
        const state: any = { totpId: socialLogin.sid, email: socialLogin.email }
        if (socialLogin.verified !== 'true' && socialLogin.uri) {
          state.totpUri = socialLogin.uri
        }
        push('/verify', state);
      } else if (socialLogin.email) {
        push('/admin/verify', { email: socialLogin.email });
      }
    },
    onError: (error, options) => {
      trackEvent(
        Events.actions.logInFailed,
        {
          email: options?.variables?.email,
          loginType: 'Microsoft',
          clientId: options?.variables?.clientId,
          message: "Login attempt failed",
          error: error.message,
        }
      );
      logger.debug(`[Error]`, error);
    }
  })

  useEffect(() => {
    // Initialize the MSAL instance
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize(); // Assuming the initialize method exists; if not, remove this line.
        setInitialized(true);
      } catch (error) {
        logger.error('Failed to initialize MSAL:', error);
      }
    };

    initializeMsal();

    // Handling redirect promise if there's a redirect from Microsoft login
    if (isInitialized) {
      msalInstance.handleRedirectPromise().then((tokenResponse: AuthenticationResult | null) => {
        if (tokenResponse) {
          // Handle the response from a redirect flow
        }
      }).catch((error: any) => {
        logger.error('Redirect error:', error);
      });
    }
  }, [isInitialized]);

  // Login function
  const signIn = async () => {
    if (!isInitialized) {
      logger.error('MSAL is not initialized, please wait.');
      return;
    }

    const loginRequest: RedirectRequest = {
      scopes: ['user.read'],  // This scope requests access to read the user's profile
      prompt: 'select_account'
    };

    try {
      // Uses loginPopup method, could also use loginRedirect for redirect behavior
      const loginResponse: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
      setMicrosoftUser({
        accessToken: loginResponse.accessToken,
        email: loginResponse.account.username,
        family_name: loginResponse.account.name?.split(' ')[0] ?? '',
        given_name: loginResponse.account.name?.split(' ')[1] ?? ''
      })
      if (type === 'Login') {
        setMicrosoftAuthLoading(true)
        await mutation({
          variables: {
            input: {
              accessToken: loginResponse.accessToken,
              clientId: localStorage.getItem('clientId'),
              provider: 'Microsoft'
            }
          }
        })
      }
    } catch (error) {
      logger.error('Login failed:', error);
    } finally {
      setMicrosoftAuthLoading(false)
    }
  };

  return {
    microsoftAuth: signIn,
    microsoftUser,
    microsoftAuthLoading,
    microsoftError: error,
    setMicrosoftUser
  }
}
